import warning from 'warning';
import deepmerge from 'deepmerge';

// Keep types lightweight to avoid cascading strictness in this legacy/themed code.
type SpacingArgument = number | string;
type SpacingMixin = ((...args: SpacingArgument[]) => number | string) & {
  mui?: boolean;
  unit?: any;
};
type Spacing = {
  touch?: SpacingMixin;
  low?: SpacingMixin;
  medium?: SpacingMixin;
  high?: SpacingMixin;
};

type SpacingInput = number | ((factor: any) => any) | SpacingMixin;

let warnOnce = false;

function muiCreateSpacing(spacingInput: SpacingInput = 8 as any): SpacingMixin {
  // If already a spacing mixin, return as-is.
  if (spacingInput && typeof spacingInput === 'function' && (spacingInput as any).mui) {
    return spacingInput as SpacingMixin;
  }

  let transform: any;
  if (typeof spacingInput === 'function') {
    transform = spacingInput;
  } else {
    warning(
      typeof spacingInput === 'number',
      [
        `Material-UI: the \`theme.spacing\` value (${spacingInput}) is invalid.`,
        'It shoud be a number or a function.'
      ].join('\n')
    );
    transform = (factor: any) => {
      warning(typeof factor === 'number', `Expected spacing argument to be a number, got ${factor}`);
      return (spacingInput as number) * factor;
    };
  }

  const spacing = ((...args: any[]) => {
    warning(args.length <= 4, `Too many arguments, expected [1, 4], got ${args.length}`);

    if (args.length === 1) {
      return transform(args[0]);
    }

    return args
      .map((factor: any) => {
        const output = transform(factor);
        return typeof output === 'number' ? `${output}px` : output;
      })
      .join(' ');
  }) as SpacingMixin;

  Object.defineProperty(spacing, 'unit', {
    get: () => {
      if (import.meta.env.DEV) {
        warning(
          !warnOnce && import.meta.env.MODE !== 'test',
          [
            'Material-UI: theme.spacing.unit usage has been depreciated.',
            'It will be removed in v5',
            'You can replace `theme.spacing.unit * y` with `theme.spacing(y)`.'
          ].join('\n')
        );
        warnOnce = true;
      }
      return spacingInput as any;
    }
  });

  spacing.mui = true;

  return spacing;
}

export const createSpacingMixinCreator = muiCreateSpacing;

export const createSpacingMixinTouch = muiCreateSpacing(16);
export const createSpacingMixinLow = muiCreateSpacing(12);
export const createSpacingMixinMedium = muiCreateSpacing(8);
export const createSpacingMixinHigh = muiCreateSpacing(4);

export const defaultSpacing: Spacing = {
  touch: createSpacingMixinTouch,
  low: createSpacingMixinLow,
  medium: createSpacingMixinMedium,
  high: createSpacingMixinHigh
};

export const createSpacing = (spacing: Spacing | ((defaultSpacing: Spacing) => Spacing) = {}) => {
  const otherOptions: Spacing = typeof spacing === 'function' ? spacing(defaultSpacing) : spacing;
  return deepmerge(defaultSpacing, otherOptions || {}, { clone: false }) as Spacing;
};
